import React, { useState } from 'react';
import { Check, Copy, Plus, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from 'shared/lib/utils';
import { ConfirmModal } from 'shared/components/ui/ConfirmModal';
import { DAYS } from 'features/onboarding/types/onboarding.types';
import type {
  DayHours,
  DayInterval,
  DayKey,
  OpeningHours,
} from 'features/onboarding/types/onboarding.types';

interface OpeningHoursPickerProps {
  value: OpeningHours;
  onChange: (next: OpeningHours) => void;
  error?: string;
  /**
   * Restrict the editor to a subset of days (e.g. the partner's selected
   * working days). Defaults to all 7 days. Days outside this list are hidden
   * and left untouched (they should be marked closed by the caller).
   */
  days?: DayKey[];
}

const FALLBACK_INTERVAL: DayInterval = { open: '09:00', close: '22:00' };
const ADDITIONAL_INTERVAL: DayInterval = { open: '17:00', close: '22:00' };

/**
 * Structured 7-day opening-hours editor.
 *
 * Keeps the original row-based Partner Connect layout (one row per interval with
 * two time inputs and a "Closed" checkbox + copy-to-all on the first row of
 * each day) while supporting split shifts: each day can hold one or more
 * intervals. An "add more hours" link under each enabled day appends a new
 * interval row, and extra rows show a trash icon (gated by a confirmation
 * modal) to remove them.
 */
const OpeningHoursPicker: React.FC<OpeningHoursPickerProps> = ({
  value,
  onChange,
  error,
  days,
}) => {
  const { t } = useTranslation();
  // Which days to render/edit. Preserve the canonical week order.
  const visibleDays: DayKey[] = days ? DAYS.filter((d) => days.includes(d)) : DAYS;
  const [pendingDelete, setPendingDelete] = useState<{
    day: DayKey;
    idx: number;
  } | null>(null);

  const errorMessage = error
    ? (t(`validation.${error}`, { defaultValue: error }) as string)
    : undefined;

  const isVisuallyClosed = (d: DayHours): boolean =>
    !d.enabled || d.mode === '24h';

  const ensureIntervals = (d: DayHours): DayInterval[] =>
    d.intervals.length > 0 ? d.intervals : [{ ...FALLBACK_INTERVAL }];

  const updateDay = (day: DayKey, partial: Partial<DayHours>) => {
    onChange({ ...value, [day]: { ...value[day], ...partial } });
  };

  const writeIntervals = (day: DayKey, intervals: DayInterval[]) => {
    const current = value[day];
    onChange({
      ...value,
      [day]: {
        ...current,
        enabled: true,
        mode: 'specific',
        intervals,
      },
    });
  };

  const setIntervalField = (
    day: DayKey,
    idx: number,
    partial: Partial<DayInterval>,
  ) => {
    const intervals = ensureIntervals(value[day]).map((it, i) =>
      i === idx ? { ...it, ...partial } : it,
    );
    writeIntervals(day, intervals);
  };

  const addInterval = (day: DayKey) => {
    const intervals = [
      ...ensureIntervals(value[day]),
      { ...ADDITIONAL_INTERVAL },
    ];
    writeIntervals(day, intervals);
  };

  const removeInterval = (day: DayKey, idx: number) => {
    const intervals = ensureIntervals(value[day]).filter((_, i) => i !== idx);
    if (intervals.length === 0) return;
    writeIntervals(day, intervals);
  };

  const toggleClosed = (day: DayKey) => {
    updateDay(day, { enabled: !value[day].enabled });
  };

  const copyToAll = (day: DayKey) => {
    const source = value[day];
    const next: OpeningHours = { ...value };
    // Only copy onto the visible (working) days so hidden/closed days aren't
    // silently reopened.
    visibleDays.forEach((d) => {
      next[d] = {
        ...source,
        intervals: source.intervals.map((i) => ({ ...i })),
      };
    });
    onChange(next);
  };

  return (
    <div className="flex flex-col gap-1.5" data-error={error ? 'true' : undefined}>
      <div className="rounded-2xl border border-gray-200 bg-white divide-y divide-gray-100 overflow-hidden">
        {visibleDays.map((day) => {
          const d = value[day];
          const closed = isVisuallyClosed(d);
          const intervals = ensureIntervals(d);
          return (
            <div
              key={day}
              className={cn(
                'flex flex-col gap-2 p-3 sm:p-4 transition-colors',
                closed ? 'bg-gray-50' : 'bg-white',
              )}
            >
              {intervals.map((interval, idx) => {
                const isFirst = idx === 0;
                return (
                  <div
                    key={idx}
                    className="flex flex-wrap items-center gap-3"
                  >
                    <span
                      className={cn(
                        'w-16 text-sm font-medium text-gray-800 flex-shrink-0',
                        !isFirst && 'invisible',
                      )}
                      aria-hidden={!isFirst}
                    >
                      {t(`openingHours.days.${day}`)}
                    </span>

                    <div className="flex items-center gap-2 flex-1 min-w-[200px]">
                      <input
                        type="time"
                        value={interval.open}
                        disabled={closed}
                        onChange={(e) =>
                          setIntervalField(day, idx, { open: e.target.value })
                        }
                        className={cn(
                          'flex-1 min-w-0 h-10 px-3 rounded-xl border bg-white text-sm font-medium text-gray-800 transition-colors',
                          'border-gray-300 focus:border-gray-400 focus:ring-2 focus:ring-gray-200 focus:outline-none',
                          closed && 'opacity-40 cursor-not-allowed',
                        )}
                      />
                      <span className="text-xs text-gray-500 px-1 flex-shrink-0">
                        {t('openingHours.to')}
                      </span>
                      <input
                        type="time"
                        value={interval.close}
                        disabled={closed}
                        onChange={(e) =>
                          setIntervalField(day, idx, { close: e.target.value })
                        }
                        className={cn(
                          'flex-1 min-w-0 h-10 px-3 rounded-xl border bg-white text-sm font-medium text-gray-800 transition-colors',
                          'border-gray-300 focus:border-gray-400 focus:ring-2 focus:ring-gray-200 focus:outline-none',
                          closed && 'opacity-40 cursor-not-allowed',
                        )}
                      />
                    </div>

                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        role="switch"
                        aria-checked={closed}
                        onClick={() => toggleClosed(day)}
                        className="inline-flex items-center gap-2 select-none"
                      >
                        <span
                          className={cn(
                            'w-5 h-5 rounded-md border flex items-center justify-center transition-colors flex-shrink-0',
                            closed
                              ? 'bg-gray-900 border-gray-900 text-white'
                              : 'bg-white border-gray-300 hover:border-gray-400',
                          )}
                        >
                          {closed && <Check size={12} strokeWidth={3} />}
                        </span>
                        <span className="text-xs font-medium text-gray-600">
                          {t('openingHours.closed')}
                        </span>
                      </button>

                      <button
                        type="button"
                        onClick={() => copyToAll(day)}
                        title={t('openingHours.copyToAll') as string}
                        className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                        aria-label={t('openingHours.copyToAll') as string}
                      >
                        <Copy size={14} />
                      </button>

                      <button
                        type="button"
                        onClick={() => setPendingDelete({ day, idx })}
                        disabled={closed || isFirst}
                        title={t('openingHours.removeInterval') as string}
                        aria-label={t('openingHours.removeInterval') as string}
                        className={cn(
                          'p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors disabled:cursor-not-allowed',
                          isFirst && 'invisible',
                        )}
                        aria-hidden={isFirst}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                );
              })}

              {!closed && (
                <button
                  type="button"
                  onClick={() => addInterval(day)}
                  className="ms-[4.75rem] self-start inline-flex items-center gap-1.5 text-xs font-medium text-gray-700 hover:text-gray-900 transition-colors"
                >
                  <Plus size={14} />
                  {t('openingHours.addInterval')}
                </button>
              )}
            </div>
          );
        })}
      </div>

      {errorMessage && (
        <span className="text-xs font-medium text-red-600">{errorMessage}</span>
      )}

      <ConfirmModal
        isOpen={pendingDelete !== null}
        variant="danger"
        title={t('openingHours.deleteIntervalTitle') as string}
        message={t('openingHours.deleteIntervalMessage') as string}
        confirmText={t('openingHours.deleteIntervalConfirm') as string}
        onClose={() => setPendingDelete(null)}
        onConfirm={() => {
          if (pendingDelete) {
            removeInterval(pendingDelete.day, pendingDelete.idx);
          }
          setPendingDelete(null);
        }}
      />
    </div>
  );
};

export default OpeningHoursPicker;
